import type { FC } from 'react';
import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Stack, Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Image from '@atlaskit/image';
import { cssMap } from '@atlaskit/css';
import Button from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';

import { ErrorDisplay } from '@confluence/error-boundary';
import {
	ExperienceTrackerContext,
	ExperienceFailure,
	AI_AUDIO_BRIEFING,
} from '@confluence/experience-tracker';
import { ConfluenceLabsLozenge } from '@confluence/ai-common';

import AudioPanelErrorIllustration from '../../assets/AudioPanelErrorIllustration.svg';
import AudioPanelErrorIllustrationDark from '../../assets/AudioPanelErrorIllustrationDark.svg';
import { useAudioBriefing } from '../../hooks/useAudioBriefing';

import { AudioBriefingPopupControlButton } from './AudioBriefingPopupControlButton';

const styles = cssMap({
	wrapper: {
		width: '300px',
	},
	errorIllustrationContainer: {
		width: '100px',
		height: '100px',
	},
	errorTextContainer: {
		textAlign: 'center',
		whiteSpace: 'pre-line',
	},
	header: {
		marginBottom: token('space.075'),
	},
});

const i18n = defineMessages({
	errorMessage: {
		id: 'audio.audio-briefing-popup.error.message',
		defaultMessage: 'We couldn’t load the audio for this briefing.',
		description: 'Error message for the audio briefings popup',
	},
	errorImgAlt: {
		id: 'audio.audio-briefing-popup.error.img.alt',
		defaultMessage: 'Audio briefings popup error illustration',
		description: 'Alt text for the audio briefings popup error illustration',
	},
	tryAgainButtonLabel: {
		id: 'audio.audio-briefing-popup.error.try-again',
		defaultMessage: 'Try again',
		description: 'Text for button that allows users to try loading the briefing again',
	},
});

type AudioBriefingsErrorComponentProps = {
	error: Error;
	audioRef: React.RefObject<HTMLAudioElement>;
	closePopup: () => void;
};

export const AudioBriefingErrorComponent: FC<AudioBriefingsErrorComponentProps> = ({
	error,
	audioRef,
	closePopup,
}) => {
	const { formatMessage } = useIntl();
	const [{ activeContent }, { fetchBriefing }] = useAudioBriefing();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const handleRetry = async () => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}

		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});
		fetchBriefing();
	};

	return (
		<Box xcss={styles.wrapper} padding="space.200">
			<ExperienceFailure name={AI_AUDIO_BRIEFING} error={error} />
			<Inline spread="space-between" alignBlock="center" xcss={styles.header}>
				<ConfluenceLabsLozenge />
				<AudioBriefingPopupControlButton closePopup={closePopup} />
			</Inline>
			<Stack alignInline="center" space="space.100">
				<Inline xcss={styles.errorIllustrationContainer} alignInline="center" alignBlock="center">
					<Image
						src={AudioPanelErrorIllustration}
						alt={formatMessage(i18n.errorImgAlt)}
						srcDark={AudioPanelErrorIllustrationDark}
					/>
				</Inline>

				<Box xcss={styles.errorTextContainer}>
					<Text size="large" weight="bold">
						{formatMessage(i18n.errorMessage)}
					</Text>
				</Box>

				<Box paddingBlock="space.050">
					<Button onClick={handleRetry}>{formatMessage(i18n.tryAgainButtonLabel)}</Button>
				</Box>

				<ErrorDisplay error={error} />
			</Stack>
		</Box>
	);
};
